import React, { useCallback, useState } from 'react';

import type { UUID } from '@aline/common';
import { FormControlLabel } from '@mui/material';
import { AlineCheckbox } from 'src/components/generic';
import { ConfirmationDialog } from 'src/components/generic/ConfirmationDialog';
import { useCurrentUserId } from 'src/gql';
import { useChangeTaskStatus } from 'src/hooks/tasks';
import { makeStyles } from 'tss-react/mui';

import { getHasOthersDecisions } from './task-review-helpers';

const useStyles = makeStyles()(theme => ({
  option: {
    marginTop: theme.spacing(2),
  },
}));

/**
 * Reopens a completed task. By default only the viewer's own decision is cleared; when other
 * people have decided, this asks first and offers to clear everyone's and start a new round.
 */
export function useReopenTask(taskId: UUID) {
  const currentUserId = useCurrentUserId();
  const { loading, markNotComplete, task } = useChangeTaskStatus(taskId);
  const [isPromptOpen, setIsPromptOpen] = useState(false);

  const requestReopen = useCallback(() => {
    if (task?.completedAt && getHasOthersDecisions(task.assignments, currentUserId))
      setIsPromptOpen(true);
    else void markNotComplete();
  }, [currentUserId, markNotComplete, task]);

  const confirmReopen = useCallback(
    async (options: { startNewRound: boolean }) => {
      await markNotComplete(options);
      setIsPromptOpen(false);
    },
    [markNotComplete]
  );

  const dismissPrompt = useCallback(() => setIsPromptOpen(false), []);

  return {
    confirmReopen,
    dismissPrompt,
    isPromptOpen,
    loading,
    nextRound: (task?.round ?? 1) + 1,
    requestReopen,
  };
}

interface ReopenTaskDialogProps {
  nextRound: number;
  onClose: () => void;
  onConfirm: (options: { startNewRound: boolean }) => Promise<void>;
  open: boolean;
}

export function ReopenTaskDialog({ open, ...props }: ReopenTaskDialogProps): JSX.Element | null {
  if (!open) return null;
  return <OpenReopenTaskDialog {...props} />;
}

// Mounted only while open, so the choice starts unchecked every time.
function OpenReopenTaskDialog({
  nextRound,
  onClose,
  onConfirm,
}: Omit<ReopenTaskDialogProps, 'open'>): JSX.Element {
  const { classes } = useStyles();
  const [startNewRound, setStartNewRound] = useState(false);
  const handleConfirm = useCallback(() => onConfirm({ startNewRound }), [onConfirm, startNewRound]);

  return (
    <ConfirmationDialog
      open
      message="Others on this task have already submitted or approved. Their decisions stay unless you clear them."
      primaryText="Reopen"
      title="Reopen this task?"
      onClickPrimary={handleConfirm}
      onClose={onClose}
    >
      <FormControlLabel
        className={classes.option}
        control={
          <AlineCheckbox
            checked={startNewRound}
            onChange={event => setStartNewRound(event.target.checked)}
          />
        }
        label={`Clear everyone's decisions and start round ${nextRound}`}
      />
    </ConfirmationDialog>
  );
}
